/**
 * Percentage-driven color intensity for every matrix cell in the tile.
 *
 * Every view of the tile shares one rule: the stability class picks the color
 * family (green = Stable, amber = Varying, red = Sporadic) and the cell's
 * percentage only sets how strong that color renders — within a family the
 * largest share gets the strongest shade and the rest scale down
 * proportionally. The cross matrix uses its combined (weakest-link) stability
 * class, so a color means the same thing in all three analysis modes.
 *
 * Each family anchors on its 500-step hex from the Tailwind palette and
 * renders as an rgba() background whose alpha is interpolated between a
 * readable floor and a bounded ceiling. A luminance check (`isDarkTextReadable`)
 * covers the general case if those bounds are ever raised.
 */

// Family anchors: the 500 steps of the status palette in tailwind.config.js.
const XYZ_FAMILY_ANCHORS = {
  X: { r: 34, g: 197, b: 94 }, // #22c55e
  Y: { r: 255, g: 152, b: 0 }, // #ff9800
  Z: { r: 239, g: 68, b: 68 }, // #ef4444
};

// There is deliberately no second anchor set: the cross matrix colors its
// cells by the SAME stability families, through the same helpers, so green /
// amber / red mean exactly one thing everywhere in the tile.

// Intensity bounds: the floor keeps even the weakest populated cell visibly
// tinted (never faint white), the ceiling keeps the strongest cell clearly
// colored while remaining light enough for dark text.
const MIN_BG_ALPHA = 0.1;
const MAX_BG_ALPHA = 0.45;

// Borders track the fill one step stronger so cell edges stay defined.
const BORDER_ALPHA_BOOST = 0.15;

/**
 * Normalize cell percentages into per-family intensities (0..1).
 *
 * Each cell's share is divided by the largest share in its own color family
 * (XYZ class), so differences within a family spread across the full range.
 *
 * @param {Array} matrixCells - The 9 backend matrix cells.
 * @returns {Object} Map of abc_xyz_class -> intensity (0..1).
 */
export const computeCellIntensities = (matrixCells) =>
  normalizeByFamily(
    matrixCells,
    (cell) => cell.xyz_class,
    (cell) => cell.abc_xyz_class,
  );

/**
 * Normalize cross-matrix cell percentages into per-family intensities (0..1).
 *
 * Identical rule to {@link computeCellIntensities} — only the field names
 * differ. The cross cell's stability family is its combined (weakest-link)
 * class and its key is the cross class, so both matrices scale their color
 * the same way.
 *
 * @param {Array} matrixCells - The 9 backend cross-matrix cells.
 * @returns {Object} Map of cross_class -> intensity (0..1).
 */
export const computeCrossIntensities = (matrixCells) =>
  normalizeByFamily(
    matrixCells,
    (cell) => cell.dominant_xyz,
    (cell) => cell.cross_class,
  );

/**
 * Divide each cell's share by the largest share in its own color family, so
 * differences within a family spread across the full intensity range.
 *
 * @param {Array} matrixCells - Backend matrix cells.
 * @param {Function} getFamily - Cell -> color family key (the XYZ class).
 * @param {Function} getKey - Cell -> the key the intensity is stored under.
 * @returns {Object} Map of cell key -> intensity (0..1).
 */
function normalizeByFamily(matrixCells, getFamily, getKey) {
  const familyMax = {};
  for (const cell of matrixCells) {
    const family = getFamily(cell);
    const current = familyMax[family] || 0;
    if (cell.percentage > current) familyMax[family] = cell.percentage;
  }

  const intensities = {};
  for (const cell of matrixCells) {
    const max = familyMax[getFamily(cell)];
    intensities[getKey(cell)] = max > 0 ? cell.percentage / max : 0;
  }
  return intensities;
}

/**
 * Whether dark text stays readable on the given rgba background over white.
 *
 * Blends the color onto the white surface and checks relative luminance,
 * so the cell can fall back to light text if the alpha bounds are raised.
 *
 * @param {{r:number,g:number,b:number}} anchor - Family anchor color.
 * @param {number} alpha - Background alpha (0..1).
 * @returns {boolean} True when dark text should be used.
 */
export const isDarkTextReadable = (anchor, alpha) => {
  const blend = (channel) => channel * alpha + 255 * (1 - alpha);
  const [r, g, b] = [blend(anchor.r), blend(anchor.g), blend(anchor.b)];
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
};

/**
 * Inline style + text tone for one matrix cell.
 *
 * @param {string} xyzClass - Stability class ('X' | 'Y' | 'Z').
 * @param {number} intensity - Normalized intensity (0..1) from
 *   {@link computeCellIntensities}.
 * @returns {{style: Object, darkText: boolean}} Background/border style and
 *   whether the standard dark text palette is readable on it.
 */
export const getCellPresentation = (xyzClass, intensity) =>
  buildCellPresentation(XYZ_FAMILY_ANCHORS[xyzClass], intensity);

/**
 * Shared alpha ramp for both matrices — the single place the cell color
 * strength is defined, so the two views always read at the same weight.
 *
 * @param {{r:number,g:number,b:number}|undefined} anchor - Family anchor color.
 * @param {number} intensity - Normalized intensity (0..1).
 * @returns {{style: Object, darkText: boolean}} Background/border style and
 *   whether the standard dark text palette is readable on it.
 */
function buildCellPresentation(anchor, intensity) {
  if (!anchor) return { style: {}, darkText: true };

  const clamped = Math.max(0, Math.min(1, intensity));
  const alpha = MIN_BG_ALPHA + clamped * (MAX_BG_ALPHA - MIN_BG_ALPHA);
  const borderAlpha = Math.min(1, alpha + BORDER_ALPHA_BOOST);

  return {
    style: {
      backgroundColor: `rgba(${anchor.r}, ${anchor.g}, ${anchor.b}, ${alpha})`,
      borderColor: `rgba(${anchor.r}, ${anchor.g}, ${anchor.b}, ${borderAlpha})`,
    },
    darkText: isDarkTextReadable(anchor, alpha),
  };
}
